import type { SuggestionPreset } from '../../TeoApi';
import { TeoEventTarget } from '../../utility/TeoEventTarget';
import type { TeoText } from '../TeoText';
import { AutoSuggestionHandler } from './AutoSuggestionHandler';
import { QuickSuggestionHandler } from './QuickSuggestionHandler';
import { SelectionSuggestionHandler, type AISuggestion } from './SelectionSuggestionHandler';

/** The configuration every AI suggestion request needs, independent of what it asks for. */
export type AiSuggestionRequestBase = {
  evaluation_profile_id: number;
  template_id: string;
  selected_options: Record<string, string>;
  use_whitelist: boolean;
  // preset: SuggestionPreset;
};

/** A prompt the API reported for a quick request, for the debug view to show. */
export type QuickPromptEntry = {
  prompt: string;
  preset?: SuggestionPreset;
  time: Date;
};

export type AIHandlerEvents = {
  quickPromptLogged: (entry: QuickPromptEntry) => void;
};

/** How many prompts are kept, so that a long session does not pile them up. */
const MAX_QUICK_PROMPTS = 20;

/**
 * The AI suggestion flows of a text, plus what they have in common.
 *
 * The flows differ in how the text to improve is chosen, so they keep their own state and
 * events; everything they share is the request configuration and applying a replacement.
 */
export class AIHandler {
  /** Suggestions for the marker the user selects in the editor. */
  public selection: SelectionSuggestionHandler;
  /** Suggestions without selecting a paragraph first, reviewed one by one. */
  public quick: QuickSuggestionHandler;
  /** The quick suggestions applied on one click, without reviewing them. */
  public auto: AutoSuggestionHandler;

  public events = new TeoEventTarget<AIHandlerEvents>();

  #quickPrompts: QuickPromptEntry[] = [];

  constructor(private text: TeoText) {
    this.selection = new SelectionSuggestionHandler(this.text, this);
    this.quick = new QuickSuggestionHandler(this.text, this);
    this.auto = new AutoSuggestionHandler(this.text, this);
  }

  /**
   * The dimension the current view works on, which decides the prompts that can be offered.
   */
  public get currentDimension(): string | undefined {
    if (this.text.store.state.view === 'style') {
      // it should be index_name but it isn't
      return this.text.style.currentInstruction?.text_property_name;
    }
    return this.text.analysis?.currentInstruction?.index_name;
  }

  /**
   * The configuration shared by every AI suggestion request, single or quick.
   * @param prompt The prompt the request is made for.
   */
  public getRequestBase(): AiSuggestionRequestBase {
    if (!this.text.state.allPromptOptionsSelected) {
      throw new Error('Text configuration is not set. Cannot send AI suggestion request.');
    }
    if (!this.text.state.currentTemplateId) {
      // should not happen as "allPromptOptionsSelected" is true
      throw new Error('Keine Konfiguration ausgewählt.');
    }

    return {
      evaluation_profile_id: this.text.core.dynamicConfig.options.evaluationProfileId,
      template_id: this.text.state.currentTemplateId,
      selected_options: this.text.state.selectedPromptOptions as Record<string, string>,
      use_whitelist: this.text.whiteListActive,
      // preset: getAISuggestionPrompt(prompt.type).type as SuggestionPreset,
    };
  }

  /** The prompts of the quick requests, newest first. Only filled with debug permission. */
  public get quickPrompts(): readonly QuickPromptEntry[] {
    return this.#quickPrompts;
  }

  public logQuickPrompt(entry: Omit<QuickPromptEntry, 'time'>): void {
    const logged: QuickPromptEntry = { ...entry, time: new Date() };
    // a new array, so that observers see a value they can compare
    this.#quickPrompts = [logged, ...this.#quickPrompts].slice(0, MAX_QUICK_PROMPTS);
    this.events.triggerEvent('quickPromptLogged', logged);
  }

  useSuggestion(suggestion: Pick<AISuggestion, 'markerId' | 'replacement'>): void {
    this.text.markers.replaceMarkerContent(suggestion.markerId, suggestion.replacement);
  }
}
